import { createContext, useContext, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X, ArrowRight, ArrowLeft, CheckCircle2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API, waLink } from "../lib/site";
import { WaIcon } from "./icons";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

const OPTION_STEPS = [
  {
    key: "kitchen_type",
    title: "Kitchen Type",
    hint: "What kind of kitchen do you have in mind?",
    options: ["Straight Kitchen", "L-Shaped Kitchen", "U-Shaped Kitchen", "Parallel Kitchen", "Island Kitchen", "Not sure yet"],
  },
  {
    key: "kitchen_size",
    title: "Kitchen Size",
    hint: "Approximately how big is your kitchen?",
    options: ["Under 60 sq ft", "60 - 100 sq ft", "100 - 150 sq ft", "150+ sq ft"],
  },
  {
    key: "layout",
    title: "Layout",
    hint: "Which description fits your space best?",
    options: ["Single wall", "Two walls", "Three walls / U-shape", "Open plan"],
  },
  {
    key: "preferred_finish",
    title: "Preferred Finish",
    hint: "What look are you drawn to?",
    options: ["Gloss", "Matte", "Wood / Textured", "Not sure yet"],
  },
  {
    key: "budget",
    title: "Budget Range",
    hint: "A comfortable range helps us guide you better.",
    options: ["Under ₹1 Lakh", "₹1 - 2 Lakh", "₹2 - 4 Lakh", "₹4 Lakh+"],
  },
];

const TOTAL = 7;

const initialData = {
  kitchen_type: "",
  kitchen_size: "",
  layout: "",
  preferred_finish: "",
  budget: "",
  city: "",
  name: "",
  phone: "",
  email: "",
};

export const EnquiryProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);
  const openEnquiry = (p) => {
    setPrefill(p || null);
    setOpen(true);
  };
  const closeEnquiry = () => setOpen(false);
  return (
    <EnquiryContext.Provider value={{ openEnquiry, closeEnquiry }}>
      {children}
      <EnquiryModal open={open} prefill={prefill} onClose={closeEnquiry} />
    </EnquiryContext.Provider>
  );
};

const EnquiryModal = ({ open, prefill, onClose }) => {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(initialData);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (open) {
      setStep(0);
      setDone(false);
      setData({ ...initialData, kitchen_type: prefill?.kitchenType || "" });
    }
  }, [open, prefill]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const optionStep = step < 5 ? OPTION_STEPS[step] : null;
  const canNext = optionStep
    ? !!data[optionStep.key]
    : step === 5
    ? data.city.trim().length > 0
    : data.name.trim().length > 1 && data.phone.replace(/\D/g, "").length >= 10;

  const submit = async () => {
    setSending(true);
    try {
      await axios.post(`${API}/leads`, {
        ...data,
        source: prefill?.source || "estimate_modal",
        kitchen_type: prefill?.kitchenType || data.kitchen_type,
      });
      setDone(true);
    } catch (e) {
      toast.error("Could not submit right now. Please WhatsApp us instead.");
    } finally {
      setSending(false);
    }
  };

  const waSummary = () => {
    const lines = [
      `Hi ${"Paramount Kitchen"}! I would like to discuss my kitchen:`,
      `• Type: ${prefill?.kitchenType || data.kitchen_type || "-"}`,
      `• Size: ${data.kitchen_size || "-"}`,
      `• Layout: ${data.layout || "-"}`,
      `• Finish: ${data.preferred_finish || "-"}`,
      `• Budget: ${data.budget || "-"}`,
      `• Location: ${data.city || "-"}`,
    ].join("\n");
    return lines;
  };

  const stepTitles = [...OPTION_STEPS.map((s) => s.title), "Location", "Contact Details"];

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[90] grid place-items-center bg-royaldeep/80 p-4 backdrop-blur-md"
          onClick={onClose}
        >
          <motion.div
            data-testid="enquiry-modal"
            initial={{ opacity: 0, y: 40, scale: 0.97 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 40, scale: 0.97 }}
            transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
            onClick={(e) => e.stopPropagation()}
            className="relative max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-3xl bg-ivory p-8 shadow-2xl"
          >
            <button
              data-testid="enquiry-close"
              onClick={onClose}
              aria-label="Close enquiry form"
              className="absolute right-5 top-5 grid h-10 w-10 place-items-center rounded-full border border-ink/10 text-ink/60 transition-colors hover:bg-ink hover:text-ivory"
            >
              <X className="h-4 w-4" />
            </button>

            {done ? (
              <div data-testid="enquiry-success" className="py-10 text-center">
                <CheckCircle2 className="mx-auto h-16 w-16 text-gold" strokeWidth={1.5} />
                <h3 className="mt-6 font-display text-3xl font-medium text-ink">Thank you.</h3>
                <p className="mx-auto mt-3 max-w-sm text-sm leading-relaxed text-ink/60">
                  Our team will contact you regarding your kitchen requirement.
                </p>
                <div className="mt-8 flex flex-col gap-3">
                  <a
                    data-testid="enquiry-whatsapp"
                    href={waLink(waSummary())}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-[#128C4B] px-6 py-3.5 text-sm font-semibold text-white transition-transform hover:-translate-y-0.5"
                  >
                    <WaIcon className="h-4 w-4" /> Continue on WhatsApp
                  </a>
                  <button
                    data-testid="enquiry-done"
                    onClick={onClose}
                    className="rounded-full border border-ink/15 px-6 py-3.5 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/5"
                  >
                    Done
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="flex items-center justify-between pr-12">
                  <p className="eyebrow text-golddim">
                    Kitchen Estimate — Step {step + 1} / {TOTAL}
                  </p>
                </div>
                <div className="mt-4 h-1 overflow-hidden rounded-full bg-ink/10">
                  <motion.div
                    className="h-full rounded-full bg-gold"
                    animate={{ width: `${((step + 1) / TOTAL) * 100}%` }}
                    transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  />
                </div>

                <AnimatePresence mode="wait">
                  <motion.div
                    key={step}
                    data-testid={`enquiry-step-${step + 1}`}
                    initial={{ opacity: 0, x: 32 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -32 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="mt-8 min-h-[240px]"
                  >
                    <h3 className="font-display text-3xl font-medium text-ink">{stepTitles[step]}</h3>

                    {optionStep && (
                      <>
                        <p className="mt-2 text-sm text-ink/55">{optionStep.hint}</p>
                        <div className="mt-6 grid grid-cols-1 gap-3 sm:grid-cols-2">
                          {optionStep.options.map((opt) => (
                            <button
                              key={opt}
                              data-testid={`enquiry-option-${opt.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                              onClick={() => {
                                set(optionStep.key, opt);
                                setTimeout(() => setStep((s) => Math.min(s + 1, TOTAL - 1)), 220);
                              }}
                              className={`rounded-xl border px-5 py-4 text-left text-sm font-medium transition-all duration-200 ${
                                data[optionStep.key] === opt
                                  ? "border-royal bg-royal text-ivory shadow-md"
                                  : "border-ink/15 bg-white text-ink/75 hover:border-gold hover:bg-sand/60"
                              }`}
                            >
                              {opt}
                            </button>
                          ))}
                        </div>
                      </>
                    )}

                    {step === 5 && (
                      <>
                        <p className="mt-2 text-sm text-ink/55">Where should we plan your kitchen?</p>
                        <input
                          data-testid="enquiry-input-location"
                          value={data.city}
                          onChange={(e) => set("city", e.target.value)}
                          placeholder="City / town (e.g. Dharamshala, Kangra)"
                          className="premium-input mt-6"
                          autoFocus
                        />
                        <p className="mt-2 text-xs text-ink/45">
                          We currently serve Kangra District, Himachal Pradesh and nearby regions.
                        </p>
                      </>
                    )}

                    {step === 6 && (
                      <div className="mt-4 space-y-4">
                        <input
                          data-testid="enquiry-input-name"
                          value={data.name}
                          onChange={(e) => set("name", e.target.value)}
                          placeholder="Your name *"
                          className="premium-input"
                        />
                        <input
                          data-testid="enquiry-input-phone"
                          value={data.phone}
                          onChange={(e) => set("phone", e.target.value)}
                          placeholder="Phone number (WhatsApp preferred) *"
                          inputMode="tel"
                          className="premium-input"
                        />
                        <input
                          data-testid="enquiry-input-email"
                          value={data.email}
                          onChange={(e) => set("email", e.target.value)}
                          placeholder="Email (optional)"
                          type="email"
                          className="premium-input"
                        />
                        <p className="text-xs text-ink/45">
                          By continuing you agree to be contacted about your kitchen requirement.
                        </p>
                      </div>
                    )}
                  </motion.div>
                </AnimatePresence>

                <div className="mt-8 flex items-center justify-between gap-3">
                  {step > 0 ? (
                    <button
                      data-testid="enquiry-back"
                      onClick={() => setStep((s) => s - 1)}
                      className="inline-flex items-center gap-2 rounded-full border border-ink/15 px-6 py-3 text-sm font-semibold text-ink/70 transition-colors hover:bg-ink/5"
                    >
                      <ArrowLeft className="h-4 w-4" /> Back
                    </button>
                  ) : (
                    <span />
                  )}
                  {step < TOTAL - 1 ? (
                    <button
                      data-testid="enquiry-next"
                      disabled={!canNext}
                      onClick={() => setStep((s) => s + 1)}
                      className="inline-flex items-center gap-2 rounded-full bg-royal px-7 py-3.5 text-sm font-semibold text-ivory shadow-md transition-all duration-300 enabled:hover:-translate-y-0.5 enabled:hover:bg-royaldeep disabled:opacity-35"
                    >
                      Next <ArrowRight className="h-4 w-4" />
                    </button>
                  ) : (
                    <button
                      data-testid="enquiry-submit"
                      disabled={!canNext || sending}
                      onClick={submit}
                      className="inline-flex items-center gap-2 rounded-full bg-gold px-7 py-3.5 text-sm font-bold text-royal shadow-md transition-all duration-300 enabled:hover:-translate-y-0.5 disabled:opacity-35"
                    >
                      {sending ? "Sending…" : "Request a Consultation"} {!sending && <ArrowRight className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
